import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription, firstValueFrom } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';

import { Person } from '../../objects/person';
import { CreateEntity } from '../create-entity';
import { DatabaseAction } from '../../objects/interface-library';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';

// PrimeNG imports
import { AutoCompleteModule } from 'primeng/autocomplete';

// Services
import { PeopleService } from '../../service/entity-services/people.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { PersonSheetsService } from '../../service/google-sheets/person-sheets.service';

import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
  selector: 'app-new-person-full-page',
  templateUrl: './new-person-full-page.component.html',
  styleUrls: ['./new-person-full-page.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinner,
    MatOption,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    AutoCompleteModule
  ]
})
export class NewPersonFullPageComponent extends CreateEntity implements OnInit, OnDestroy {
  private commonSheetsService = inject(CommonSheetsService);
  private personSheetsService = inject(PersonSheetsService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;

  currentPerson: Person = {} as Person;
  originalPerson: any = {};

  // UI State
  showAdditionalDetails = false;
  isEditMode = false;

  // Static field data
  /**
   * The COMPLETE party list, kept separate from `filteredParties` (which the
   * typeahead narrows as the user types). Lookups must resolve against the full
   * list — searching the narrowed one would fail to resolve a saved vendor as
   * soon as the user had typed anything.
   */
  allPartiesList: any[] = [];

  prefixLabels: any[] = [];
  suffixLabels: any[] = [];
  titleLabels: any[] = [];
  functionLabels: any[] = [];
  geographicStates: any[] = [];
  departments: any[] = [];

  // Filtered autocomplete lists
  filteredPrefixes: any[] = [];
  filteredSuffixes: any[] = [];
  filteredTitles: any[] = [];
  filteredFunctions: any[] = [];
  filteredStates: any[] = [];
  filteredDepartments: any[] = [];

  saving = false;
  loadingPersonData = false;

  constructor(
    public dialog: MatDialog,
    public router: Router,
    private fb: FormBuilder
  ) {
    super();

    // Initialize the form with validation
    this.form = this.fb.group({
      // ID and metadata
      contactNum: [''],
      entityType: ['person'],

      // Name fields
      prefix: [''],
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      suffix: [''],
      salutation: [''],
      fullName: [''],
      friendlyName: [''],

      // Professional info
      title: [''],
      function: [''],
      department: [''],

      // Contact information
      email: ['', [Validators.email]],
      email2: ['', [Validators.email]],
      primaryPhone: [''],
      officePhone: [''],
      mobilePhone: [''],
      secondaryPhone: [''],
      faxPhone: [''],

      // Primary address
      address: [''],
      city: [''],
      state: [''],
      zip: [''],

      // Secondary address
      secondaryAddress: [''],
      secondaryCity: [''],
      secondaryState: [''],
      secondaryZip: [''],

      // Relationships
      primaryVendor: [''],
      primaryVendorFriendlyName: [''],
      primaryResponsibleParty: [''],
      primaryOwner: [''],
      secondaryOwner: [''],
      tertiaryOwner: [''],

      // Arrays (managed separately)
      vendors: [[]],
      people: [[]],
      baas: [[]],
      cois: [[]],
      contracts: [[]],
      parties: [[]],
      attachments: [[]],
      criticalDates: [[]],
      notes: [[]],
      riskAssessments: [[]],
      vendorFriendlyNames: [[]],

      // Additional fields
      description: [''],
      other: [''],
      versionNum: [null]
    });
  }

  async ngOnInit() {
    this.loadingPersonData = true;

    try {
      const contactNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data for dropdown filtering.
      //
      // settledCache, NOT firstValueFrom: allParties$ re-emits a GROWING array on
      // every page of the paginated fetch, so `firstValueFrom` captured page one —
      // roughly the first 100 of cbhi's ~4,500 parties — and never updated. The
      // picker then looked as though it excluded whole categories of party (the
      // reported symptom was "non-NetSuite parties are missing"), when in truth it
      // was showing an arbitrary early slice. settledCache waits for the stream to
      // go quiet and hands back the complete list.
      this.filteredUsers = await settledCache(this.allUsers$);
      this.allPartiesList = await settledCache(this.allParties$);
      this.filteredParties = this.allPartiesList;

      if (contactNum) {
        // Load person from service using ID
        this.isEditMode = true;
        await this.loadPersonByContactNum(contactNum);
      } else if (state?.person) {
        // Person passed via router state
        this.isEditMode = true;
        this.currentPerson = state.person;
        this.originalPerson = { ...state.person };
        this.populateFormWithPerson(this.currentPerson);
      } else {
        this.isEditMode = false;
        // Brand new person gets new contactNum
        this.form.patchValue({
          contactNum: Date.now().toString()
        });
      }

      // Subscribe to form changes
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentPerson = { ...this.currentPerson, ...changes };

        // Auto-generate fullName when name fields change
        if (changes.firstName || changes.middleName || changes.lastName) {
          this.updateFullName();
        }
      });

    } finally {
      this.loadingPersonData = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async loadPersonByContactNum(contactNum: string) {
    try {
      // Get person from service. settledCache, not firstValueFrom — allPeople$ is
      // paginated the same way as parties, so a person outside the first page hit
      // the "not found" branch and the edit bounced back to the people list.
      const people = await settledCache(this.peopleService.allPeople$);
      const person = people.find(p => p.contactNum === contactNum);

      if (person) {
        //console.log('Person found:', person);

        // Get fully hydrated person
        const hydratedPerson = await firstValueFrom(
          this.peopleService.getPersonFullyHydrated(contactNum)
        );

        if (hydratedPerson) {
          this.currentPerson = hydratedPerson;
          this.originalPerson = { ...hydratedPerson };
          this.populateFormWithPerson(hydratedPerson);
        } else {
          console.error('Failed to hydrate person');
          alert('Failed to load person details');
          this.router.navigate(['/securehome/people']);
        }
      } else {
        console.error('Person not found with contactNum:', contactNum);
        alert('Person not found');
        this.router.navigate(['/securehome/people']);
      }
    } catch (error) {
      console.error('Error loading person:', error);
      alert('Failed to load person');
    }
  }

  populateFormWithPerson(person: Person) {
    //console.log('Populating form with person:', person);

    // Convert primaryVendor from string ID to object for autocomplete display
    const primaryVendorObject = this.extractVendorForUI(person.primaryVendor);

    // Convert primaryResponsibleParty from string ID to object for autocomplete display
    const primaryResponsiblePartyObject = this.extractUserForUI(person.primaryResponsibleParty);

    this.form.patchValue({
      contactNum: person.contactNum,
      entityType: person.entityType || 'person',
      prefix: person.prefix,
      firstName: person.firstName,
      middleName: person.middleName,
      lastName: person.lastName,
      suffix: person.suffix,
      salutation: person.salutation,
      fullName: person.fullName,
      friendlyName: person.friendlyName,
      title: person.title,
      function: person.function,
      department: person.department,
      email: person.email,
      email2: person.email2,
      primaryPhone: person.primaryPhone,
      officePhone: person.officePhone,
      mobilePhone: person.mobilePhone,
      secondaryPhone: person.secondaryPhone,
      faxPhone: person.faxPhone,
      address: person.address,
      city: person.city,
      state: person.state,
      zip: person.zip,
      secondaryAddress: person.secondaryAddress,
      secondaryCity: person.secondaryCity,
      secondaryState: person.secondaryState,
      secondaryZip: person.secondaryZip,
      primaryVendor: primaryVendorObject,
      primaryVendorFriendlyName: person.primaryVendorFriendlyName,
      primaryResponsibleParty: primaryResponsiblePartyObject,
      primaryOwner: person.primaryOwner,
      secondaryOwner: person.secondaryOwner,
      tertiaryOwner: person.tertiaryOwner,
      vendors: Array.isArray(person.vendors) ? person.vendors : [],
      people: Array.isArray(person.people) ? person.people : [],
      baas: Array.isArray(person.baas) ? person.baas : [],
      cois: Array.isArray(person.cois) ? person.cois : [],
      contracts: Array.isArray(person.contracts) ? person.contracts : [],
      parties: Array.isArray(person.parties) ? person.parties : [],
      attachments: Array.isArray(person.attachments) ? person.attachments : [],
      criticalDates: Array.isArray(person.criticalDates) ? person.criticalDates : [],
      notes: Array.isArray(person.notes) ? person.notes : [],
      riskAssessments: Array.isArray(person.riskAssessments) ? person.riskAssessments : [],
      vendorFriendlyNames: Array.isArray(person.vendorFriendlyNames) ? person.vendorFriendlyNames : [],
      description: person.description,
      other: person.other,
      versionNum: person.versionNum
    });
  }

  /**
   * Converts a primaryVendor value (string ID or object) to a flat vendor object for autocomplete.
   * Returns the vendor object from the COMPLETE party list, or null if not found.
   */
  private extractVendorForUI(primaryVendor: any): any {
    if (!primaryVendor) return null;

    // If it's already an object with name property, use it directly
    if (typeof primaryVendor === 'object' && primaryVendor.name) {
      // Check if it's wrapped (has vendorData)
      if (primaryVendor.vendorData) {
        const vendorNum = primaryVendor.vendorNum || primaryVendor.vendorData?.vendorNum;
        return this.allPartiesList.find(p => p.vendorNum === vendorNum) || null;
      }
      // Already flat, find matching object in filteredParties for consistency
      return this.allPartiesList.find(p => p.vendorNum === primaryVendor.vendorNum) || primaryVendor;
    }

    // If it's a string (vendorNum), look up in filteredParties
    if (typeof primaryVendor === 'string') {
      return this.allPartiesList.find(p => p.vendorNum === primaryVendor) || null;
    }

    return null;
  }

  /**
   * Converts a primaryResponsibleParty value (string ID or object) to a user object for autocomplete.
   * Returns the user object from filteredUsers, or null if not found.
   */
  private extractUserForUI(user: any): any {
    if (!user) return null;

    // If it's already an object with shortFriendlyName, find in filteredUsers
    if (typeof user === 'object') {
      const userNum = user.userNum || user.userId;
      if (userNum) {
        return this.filteredUsers.find(u => u.userNum === userNum || u.userId === userNum) || user;
      }
      return user;
    }

    // If it's a string (userNum or email), look up in filteredUsers
    if (typeof user === 'string') {
      return this.filteredUsers.find(u =>
        u.userNum === user || u.userId === user || u.email === user
      ) || null;
    }

    return null;
  }

  updateFullName() {
    const firstName = this.form.get('firstName')?.value || '';
    const middleName = this.form.get('middleName')?.value || '';
    const lastName = this.form.get('lastName')?.value || '';

    let fullName = firstName;
    if (middleName) {
      fullName += ' ' + middleName;
    }
    if (lastName) {
      fullName += ' ' + lastName;
    }

    this.form.patchValue({ fullName: fullName.trim() }, { emitEvent: false });
  }

  async getStaticFieldValues() {
    try {
      // Load person-specific data
      this.prefixLabels = await firstValueFrom(
        this.personSheetsService.getPrefixLabels()
      );
      this.filteredPrefixes = [...this.prefixLabels];

      this.suffixLabels = await firstValueFrom(
        this.personSheetsService.getSuffixLabels()
      );
      this.filteredSuffixes = [...this.suffixLabels];

      this.titleLabels = await firstValueFrom(
        this.personSheetsService.getTitleLabels()
      );
      this.filteredTitles = [...this.titleLabels];

      this.functionLabels = await firstValueFrom(
        this.personSheetsService.getPersonFunctionLabels()
      );
      this.filteredFunctions = [...this.functionLabels];

      // Load common data
      this.geographicStates = await firstValueFrom(
        this.commonSheetsService.getGeographicStates()
      );
      this.filteredStates = [...this.geographicStates];

      this.departments = await firstValueFrom(
        this.commonSheetsService.getDepartments()
      );
      this.filteredDepartments = [...this.departments];

    } catch (error) {
      console.error('Error loading static field values:', error);
    }
  }

  // Filter methods for autocomplete
  filterPrefixes(event: any) {
    const query = event.query.toLowerCase();
    this.filteredPrefixes = this.prefixLabels.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  filterSuffixes(event: any) {
    const query = event.query.toLowerCase();
    this.filteredSuffixes = this.suffixLabels.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  filterTitles(event: any) {
    const query = event.query.toLowerCase();
    this.filteredTitles = this.titleLabels.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  filterFunctions(event: any) {
    const query = event.query.toLowerCase();
    this.filteredFunctions = this.functionLabels.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  filterStates(event: any) {
    const query = event.query.toLowerCase();
    this.filteredStates = this.geographicStates.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  filterDepartments(event: any) {
    const query = event.query.toLowerCase();
    this.filteredDepartments = this.departments.filter(option =>
      option.toLowerCase().includes(query)
    );
  }

  // Display functions for autocomplete
  displayUserFn(user: any): string {
    return user && user.shortFriendlyName ? user.shortFriendlyName : '';
  }

  displayPartyFn(party: any): string {
    return party && party.name ? party.name : '';
  }

  // Validation
  isFormValid(): boolean {
    const requiredFields = ['firstName', 'lastName'];
    return requiredFields.every(field => this.form.get(field)?.value);
  }

  getValidationMessage(): string {
    if (!this.form.get('firstName')?.value) return 'First name is required';
    if (!this.form.get('lastName')?.value) return 'Last name is required';
    return '';
  }

  toggleAdditionalDetails() {
    this.showAdditionalDetails = !this.showAdditionalDetails;
  }

  cancelSubmit() {
    this.router.navigateByUrl('/securehome/people');
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const personToSave: Person = this.form.value;

    // Ensure contactNum exists
    if (!personToSave.contactNum) {
      personToSave.contactNum = Date.now().toString();
    }

    // Ensure fullName is set
    if (!personToSave.fullName) {
      this.updateFullName();
      personToSave.fullName = this.form.get('fullName')?.value;
    }

    // Extract IDs from object fields
    // Primary Vendor - extract vendorNum and set friendlyName
    if (personToSave.primaryVendor && typeof personToSave.primaryVendor === 'object') {
      const vendorObj = personToSave.primaryVendor as any;
      // Save the friendly name for display
      personToSave.primaryVendorFriendlyName = vendorObj.name || vendorObj.friendlyName || '';
      // Extract just the ID for storage
      personToSave.primaryVendor = vendorObj.vendorNum || personToSave.primaryVendor;
    }

    // User/People fields - extract userNum
    if (personToSave.primaryResponsibleParty && typeof personToSave.primaryResponsibleParty === 'object') {
      const userObj = personToSave.primaryResponsibleParty as any;
      personToSave.primaryResponsibleParty = userObj.userNum || userObj.userId || personToSave.primaryResponsibleParty;
    }

    //console.log('Saving person:', personToSave);

    const action = this.isEditMode ? DatabaseAction.Edit : DatabaseAction.Create;

    this.peopleService.writePerson(personToSave).subscribe({
      next: (response) => {
        //console.log('Person saved successfully', response);

        // Manually refresh the cache and wait for it to complete before navigating
        this.peopleService.refreshAllPeopleRecords().subscribe({
          next: () => {
            this.saving = false;
            this.router.navigate(['/securehome/person', personToSave.contactNum]);
          },
          error: (err) => {
            console.error('Cache refresh failed, but navigating anyway:', err);
            this.saving = false;
            this.router.navigate(['/securehome/person', personToSave.contactNum]);
          }
        });
      },
      error: (err) => {
        console.error('Failed to save person:', err);
        this.saving = false;
        alert('Failed to save person. Please try again.');
      }
    });
  }
}
